/// <reference lib="webworker" />
import { DocumentStructure, PageStructure, TextRect } from '../models'
import { Rect } from '@st/geom'

import * as Comlink from 'comlink'
import * as mupdf from 'mupdf'

export const MUPDF_LOADED = 'MUPDF_LOADED'

export class MuPDFWorker {
  private document?: mupdf.PDFDocument
  private documentStructure?: DocumentStructure

  constructor() {
    this.initializeMuPDF()
  }

  private initializeMuPDF() {
    try {
      postMessage(MUPDF_LOADED)
    } catch (error) {
      console.error('Failed to initialize MuPDF:', error)
    }
  }

  loadDocument(document: ArrayBuffer): DocumentStructure {
    this.document = time(
      'openDocument',
      () => mupdf.PDFDocument.openDocument(document, 'application/pdf') as mupdf.PDFDocument
    )
    this.documentStructure = time('getDocumentStructure', () => this.getDocumentStructure())

    return this.documentStructure!
  }

  /**
   * Render the requested page at the given scale and return an ImageBitmap that
   * can be transferred to the main thread without any re‑encoding.
   *
   * The bitmap is created by drawing the Pixmap into a scratch OffscreenCanvas
   * inside the worker and then calling `transferToImageBitmap()`, which hands
   * off the GPU resource with zero pixel copies across threads.
   */
  renderPageAsImage(pageNumber: number, scale: number): ImageBitmap {
    if (!this.document) {
      throw new Error('Document not loaded')
    }

    // Load the PDF page (zero‑based index in MuPDF)
    const page = this.document.loadPage(pageNumber - 1)

    // Rasterise to a Pixmap in the MuPDF WASM heap
    //
    // The last argument alpha=true which makes converting to an ImageBitmap
    // more seamless because the conversion expects 4 bit color components (RGBA)
    //
    // Not doing so will result in an exception:
    // IndexSizeError: Failed to construct 'ImageData: The input data length is not equal to (4 * width * height)
    const pixmap = time('toPixmap', () =>
      page.toPixmap([scale, 0, 0, scale, 0, 0], mupdf.ColorSpace.DeviceRGB, true)
    )

    const imageBitmap = time('pixmapToImageBitmap', () => pixmapToImageBitmap(pixmap))

    pixmap.destroy()

    return imageBitmap
  }

  getDocumentStructure(): DocumentStructure {
    if (!this.document) {
      throw new Error('Document not loaded')
    }

    const pageCount = this.document.countPages()

    const pages = []

    for (let pageNumber = 1; pageNumber <= pageCount; pageNumber++) {
      pages.push(this.getPageStructure(pageNumber))
    }

    return { pages }
  }

  private getPageStructure(pageNumber: number): PageStructure {
    if (!this.document) {
      throw new Error('Document not loaded')
    }

    const page = this.document.loadPage(pageNumber - 1)
    const stext = page.toStructuredText()
    const bounds = page.getBounds()

    const results: TextRect[] = []
    let currentLine: TextRect | null = null

    /* Walk streams glyph‑level data; we build one TextRect per line */
    stext.walk({
      beginLine: (bbox /* [x0,y0,x1,y1] */) => {
        currentLine = {
          contents: '',
          bounds: boundsToRect(bbox),
          charBounds: []
        }
      },

      onChar: (utf, _origin, _fontInfo, _size, quad) => {
        if (!currentLine) return

        // append text
        currentLine.contents += utf

        // quad -> axis-aligned rect
        const xs = [quad[0], quad[2], quad[4], quad[6]]
        const ys = [quad[1], quad[3], quad[5], quad[7]]
        currentLine.charBounds.push({
          x: Math.min(...xs),
          y: Math.min(...ys),
          width: Math.max(...xs) - Math.min(...xs),
          height: Math.max(...ys) - Math.min(...ys)
        })
      },

      endLine: () => {
        if (!currentLine) return
        currentLine.contents = currentLine.contents.trim()
        if (currentLine.contents.length) results.push(currentLine)
        currentLine = null
      }
    })

    return {
      pageNumber,
      bounds: boundsToRect(bounds),
      textRects: results
    }
  }
}

function boundsToRect(bounds: [number, number, number, number]): Rect {
  return {
    x: bounds[0],
    y: bounds[1],
    width: bounds[2] - bounds[0],
    height: bounds[3] - bounds[1]
  }
}

function pixmapToImageBitmap(pixmap: mupdf.Pixmap): ImageBitmap {
  const width = pixmap.getWidth()
  const height = pixmap.getHeight()

  // Create a temporary canvas inside the worker to hold the pixels
  const canvas = new OffscreenCanvas(width, height)
  const ctx = canvas.getContext('2d')!

  // Wrap the Pixmap’s bytes in ImageData and blit to the canvas
  // By using a Uint8ClampedArray, we can effectively avoid any expensive encoding operations
  // such as converting to PNG/JPEG
  const imageData = new ImageData(new Uint8ClampedArray(pixmap.getPixels()), width, height)
  ctx.putImageData(imageData, 0, 0)

  // We are getting RGBA values from MuPDF (red, green, blue, alpha/transparency)
  // In all PDF viewers (like Adobe) a transparent pixel is interpreted as white
  // To mimic this behavior, we need to paint a white background over the entire canvas
  // so that transparent pixels are interpreted as white.
  //
  // (This makes sense because we assume a piece of paper is white and we are effectively printing the document on it)
  ctx.globalCompositeOperation = 'destination-over'
  ctx.fillStyle = '#fff'
  ctx.fillRect(0, 0, width, height)
  ctx.globalCompositeOperation = 'source-over'

  // Transfer bitmap to the caller
  // This is a zero-copy operation which means we don't pay the overhead of memory copy
  // across the web worker boundary
  return canvas.transferToImageBitmap()
}

function time<T>(label: string, fn: () => T): T {
  const start = performance.now()
  const result = fn()
  console.log(`${label}`, `${(performance.now() - start).toFixed(2)}ms`)
  return result
}

Comlink.expose(new MuPDFWorker())
